import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PlotlyModule } from 'angular-plotly.js';

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  imports: [FormsModule, CommonModule, MainNavbarComponent, MatTooltipModule, PlotlyModule],
  standalone: true,
  styleUrls: ['./chatbot.component.css']
})
export class ChatbotComponent implements OnInit {

  @ViewChild('chatBox') private chatBox!: ElementRef<HTMLDivElement>;

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.fetchSessions();
    this.active_conversation_id = '';
  }

  messages: { sender: string, text: string | SafeHtml, charts?: any[] }[] = [
    {
      sender: 'assistant',
      text: this.sanitizer.bypassSecurityTrustHtml(
        "Hi! I am SIA Assistant. How can I assist you with patent-based innovation research for public equities today? If you have questions about companies, technologies, or investment trends based on patent data, just let me know.".replace(/\n/g, '<br>')
      )
    }
  ];

  sideBarChange() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  trackByConvId(_: number, item: { conversation_id: string }) {
    return item.conversation_id;
  }

  trackByMsgIndex(index: number) {
    return index;
  }

  active_conversation_id: string = '';
  sidebarOpen = true;
  conversations: { conversation_id: string; preview: string; is_deleted: boolean }[] = [];

  private getAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: "Bearer " + localStorage.getItem("current_user_token")
    });
  }

  fetchSessions() {
    this.http.get<any>(`/backend/siaAssistant/assistant/conversations`, {
      params: { user_id: localStorage.getItem('user_id')! },
      headers: this.getAuthHeaders()
    }).subscribe({
      next: (res) => {
        this.conversations = res.conversations;
      },
      error: () => {}
    });
  }

  userInput: string = '';
  models = ['gpt-4.1', 'gpt-4.1-nano', 'gpt-4.1-mini', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'];
  selectedModel = this.models[0];

  private formatResponse(text: string): string {
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const standaloneUrlRegex = /(?<!href=")(https?:\/\/[^\s<]+[^.,;:!?\s<])(?!")/g;

    return text
      .replace(markdownLinkRegex, '<a href="$2" target="_blank">$1</a>')
      .replace(standaloneUrlRegex, '<a href="$1" target="_blank">$1</a>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  startNewChat() {
    this.messages = [];
    this.messages.push({
      sender: 'assistant',
      text: this.sanitizer.bypassSecurityTrustHtml(
        "Hi! I am SIA Assistant. How can I assist you with patent-based innovation research for public equities today? If you have questions about companies, technologies, or investment trends based on patent data, just let me know.".replace(/\n/g, '<br>')
      )
    });
    this.active_conversation_id = '';
  }

  loadSession(session: any) {
    this.messages = [];
    this.active_conversation_id = session.conversation_id;

    this.http.get<any>(`/backend/siaAssistant/assistant/conversation_history`, {
      params: { conversation_id: this.active_conversation_id! },
      headers: this.getAuthHeaders()
    }).subscribe({
      next: (res) => {
        for (const entry of res.conversation) {
          if (entry.user) {
            this.messages.push({
              sender: 'user',
              text: entry.user
            });
          }

          if (entry.assistant) {
            const charts = (entry.charts || []).map((c: any) => this.buildChartData(c));
            this.messages.push({
              sender: 'assistant',
              text: this.sanitizer.bypassSecurityTrustHtml(this.formatResponse(entry.assistant)),
              charts: charts.length ? charts : undefined
            });
          }
        }

        this.scrollToBottom();
      },
      error: () => {}
    });
  }


  deleteConversation(conversation_id: string) {
    this.http.delete(
      `/backend/siaAssistant/deleteSession/${conversation_id}`,
      { headers: this.getAuthHeaders() }
    ).subscribe({
      next: () => {
        if (conversation_id === this.active_conversation_id) {
          this.startNewChat();
        }
        this.fetchSessions();
      },
      error: () => {
        this.fetchSessions();
      }
    });
  }
  isLoading = false;

  suggestions = [
    'How innovative is Tesla?',
    'Top companies in quantum computing?',
    'How has Apple\'s innovation changed over time?',
    'Who leads in solid state batteries?',
  ];

  selectSuggestion(suggestion: string) {
    this.userInput = suggestion;
    this.sendMessage();
  }

  sendMessage() {
    if (!this.userInput.trim() || this.isLoading) return;

    this.messages.push({ sender: 'user', text: this.userInput });
    this.scrollToBottom();
    const currentInput = this.userInput;
    this.userInput = '';
    this.isLoading = true;

    this.http.post<any>(
      `/backend/siaAssistant/assistant?conversation_id=${this.active_conversation_id}`,
      {
        user_message: currentInput,
        model: this.selectedModel
      },
      { headers: this.getAuthHeaders() }
    ).subscribe({
      next: (res) => {
        const newId = res.payload.conversation_id;

        if (!this.active_conversation_id) {
          this.active_conversation_id = newId;
        }

        if (this.active_conversation_id == res.payload.conversation_id) {
          const charts = (res.payload.charts || []).map((c: any) => this.buildChartData(c));
          this.messages.push({
            sender: 'assistant',
            text: this.sanitizer.bypassSecurityTrustHtml(this.formatResponse(res.payload.response)),
            charts: charts.length ? charts : undefined
          });
          this.scrollToBottom();
        }

        this.isLoading = false;
        this.fetchSessions();
      },
      error: () => {
        this.messages.push({ sender: 'assistant', text: 'Sorry, something went wrong. Please try again later.' });
        this.isLoading = false;
        this.fetchSessions();
      }
    });
  }

  private scrollToBottom() {
    setTimeout(() => {
      if (this.chatBox?.nativeElement) {
        const el = this.chatBox.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    });
  }

  buildChartData(chart: any): { data: any[], layout: any, config: any } | null {
    if (chart.type !== 'innovation_history' || !chart.data?.length) return null;

    const dates = chart.data.map((d: any) => d.date_eom);
    const scores = chart.data.map((d: any) => d.innovation_score);
    const title = chart.entity_name || chart.entityid || 'Innovation Score';

    // Backend returns pre-indexed series (first valid point = 100).
    const mcapIndexed: (number | null)[] = chart.data.map((d: any) => d.stockprice_indexed);
    const hasMcap = mcapIndexed.some((v: any) => v != null);

    // Primary Y: market cap indexed (or innovation score if no mcap)
    const primaryY = hasMcap ? mcapIndexed : scores;
    const primaryLabel = hasMcap ? 'Market Cap (Indexed)' : 'Innovation Score';
    const primaryHover = hasMcap
      ? '<b>Market Cap (Indexed)</b>: %{y}<extra></extra>'
      : '<b>Innovation Score</b>: %{y:.1f}<extra></extra>';

    // Signal markers positioned on primary axis
    const getSignalY = (i: number) => hasMcap ? mcapIndexed[i] : scores[i];

    const buyIdx = chart.data.map((d: any, i: number) => d.signal === 'BUY' ? i : -1).filter((i: number) => i >= 0 && getSignalY(i) != null);
    const sellIdx = chart.data.map((d: any, i: number) => d.signal === 'SELL' ? i : -1).filter((i: number) => i >= 0 && getSignalY(i) != null);

    const data: any[] = [
      {
        x: dates, y: primaryY, name: primaryLabel, type: 'scatter', mode: 'lines+markers',
        line: { color: '#1e293b', width: 2.5, shape: 'spline' },
        marker: { size: 5, color: '#1e293b' },
        fill: 'tozeroy', fillcolor: 'rgba(30, 41, 59, 0.04)',
        connectgaps: true,
        hovertemplate: primaryHover,
        showlegend: false, yaxis: 'y1',
      },
      {
        x: buyIdx.map((i: number) => dates[i]), y: buyIdx.map((i: number) => getSignalY(i)),
        name: 'BUY', type: 'scatter', mode: 'markers',
        marker: { color: '#16a34a', size: 12, symbol: 'triangle-up', line: { color: '#fff', width: 1 } },
        hovertemplate: '<b>BUY</b> %{x|%b %Y}<extra></extra>',
        showlegend: false, yaxis: 'y1',
      },
      {
        x: sellIdx.map((i: number) => dates[i]), y: sellIdx.map((i: number) => getSignalY(i)),
        name: 'SELL', type: 'scatter', mode: 'markers',
        marker: { color: '#dc2626', size: 12, symbol: 'triangle-down', line: { color: '#fff', width: 1 } },
        hovertemplate: '<b>SELL</b> %{x|%b %Y}<extra></extra>',
        showlegend: false, yaxis: 'y1',
      },
    ];

    // Secondary Y: innovation score (only when market cap is the primary)
    if (hasMcap) {
      data.push({
        x: dates, y: scores, name: 'Innovation Score', type: 'scatter', mode: 'lines+markers',
        line: { color: '#3b82f6', width: 2, shape: 'spline' },
        marker: { size: 4, color: '#3b82f6' },
        hovertemplate: '<b>Innovation Score</b>: %{y:.1f}<extra></extra>',
        showlegend: false, yaxis: 'y2',
      });
    }

    const layout: any = {
      height: 320, margin: { l: 50, r: hasMcap ? 60 : 16, t: 36, b: 50 },
      title: { text: title, font: { size: 14, color: '#333' }, x: 0.02, xanchor: 'left' },
      xaxis: { type: 'date', tickangle: -45, gridcolor: '#eee', tickfont: { size: 11 } },
      yaxis: {
        title: { text: primaryLabel, font: { size: 12, color: '#1e293b' } },
        range: hasMcap ? undefined : [0, 105],
        gridcolor: '#eee', tickfont: { size: 11 },
      },
      plot_bgcolor: '#fff', paper_bgcolor: '#fff',
      showlegend: false,
      hovermode: 'x unified',
      hoverlabel: { bgcolor: '#1e293b', font: { color: '#fff', size: 12 } },
    };

    if (hasMcap) {
      layout.yaxis2 = {
        title: { text: 'Innovation Score', font: { size: 12, color: '#3b82f6' } },
        overlaying: 'y', side: 'right', range: [0, 105],
        showgrid: false, tickfont: { size: 11, color: '#3b82f6' },
      };
    }

    const config = {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['toImage', 'lasso2d', 'select2d'] as any,
      displaylogo: false,
    };

    return { data, layout, config };
  }

}
